import { Text, Spinner } from '@belong/ui';
import { clsx } from 'clsx';
import type { UseIntroVideoResult } from 'hooks/use-intro-video';
import { formatString } from 'strings';
import { POST_INSPECTION_FLOW_STRINGS } from 'strings/post-inspection-flow';
import styles from './intro-video-cta.module.css';

const VIDEO_SRC =
  'https://belong-consumer-assets.s3.us-west-1.amazonaws.com/modals/belong-standards/belong_standard_video.mp4';

type Props = {
  video: UseIntroVideoResult;
  onContinue: () => void;
  ctaLabel: string;
};

function WatchCountdown({ remainingTime }: { remainingTime: string }) {
  return (
    <Text variant="p1">
      {POST_INSPECTION_FLOW_STRINGS['intro.video.countdown']}{' '}
      <Text as="span" fontWeight="semibold" variant="p1">
        {formatString(
          POST_INSPECTION_FLOW_STRINGS['intro.video.countdown_remaining'],
          { remainingTime },
        )}
      </Text>
    </Text>
  );
}

export function IntroVideoCta({ video, onContinue, ctaLabel }: Props) {
  const {
    videoRef,
    isLoading,
    isUnlocked: hasWatchedVideo,
    progress,
    remainingTime,
    showCountdown,
    toggleMute,
    videoProps,
  } = video;

  return (
    <>
      <div
        className="relative mb-xl cursor-pointer overflow-hidden rounded border border-solid border-gray"
        role="button"
        tabIndex={0}
        onClick={toggleMute}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleMute();
          }
        }}
        onMouseEnter={() => {
          const isDesktop = window.matchMedia('(min-width: 768px)').matches;
          if (isDesktop && videoRef.current) videoRef.current.controls = true;
        }}
        onMouseLeave={() => {
          if (videoRef.current) videoRef.current.controls = false;
        }}
      >
        <video
          ref={videoRef}
          className="block min-h-[180px] w-full"
          src={VIDEO_SRC}
          preload="auto"
          muted
          playsInline
          {...videoProps}
        />
        {isLoading && <Spinner />}
      </div>

      <div className="mb-3xl hidden flex-col items-center gap-xs md:flex">
        <button
          className={styles.progressButton}
          onClick={() => onContinue()}
          disabled={!hasWatchedVideo}
        >
          <div
            className={styles.progressFill}
            style={{ width: `${hasWatchedVideo ? 100 : progress}%` }}
          />
          <Text fontWeight="semibold" className="relative z-10 text-white">
            {ctaLabel}
          </Text>
        </button>
        {!hasWatchedVideo && showCountdown && (
          <WatchCountdown remainingTime={remainingTime} />
        )}
      </div>
    </>
  );
}

// Sits three sections below IntroVideoCta in the layout, after Belong
// Standards, so it cannot simply be rendered by that component.
export function IntroMobileCta({ video, onContinue, ctaLabel }: Props) {
  const { isUnlocked: hasWatchedVideo, progress, remainingTime } = video;

  return (
    <div className="my-2xl w-full md:hidden">
      <div className={clsx('py-xs text-center', styles.mobileTimerBg)}>
        <WatchCountdown remainingTime={remainingTime} />
      </div>
      <button
        className={styles.mobileProgressButton}
        onClick={() => onContinue()}
        disabled={!hasWatchedVideo}
      >
        <div
          className={styles.progressFill}
          style={{ width: `${hasWatchedVideo ? 100 : progress}%` }}
        />
        <Text fontWeight="semibold" className="relative z-10 text-white">
          {ctaLabel}
        </Text>
      </button>
    </div>
  );
}
